/*
 * HomeLand: keeps branding add-on lines (product type "Add-on") in step with their garment.
 *
 * Add-ons are added as nested lines (parent_id), so Shopify already removes them when the
 * garment is removed. Shopify does NOT change their quantity when the garment's quantity
 * changes, and an add-on can end up alone (added some other way). After every cart change,
 * and once on load when the server flagged a mismatch (data-hl-cart-fix), this:
 *   - sets each add-on's quantity to its garment's quantity (parent line, else same _hl_group)
 *   - removes an add-on with no garment (an orphan)
 * in one /cart/update.js call, then dispatches Horizon's CartLinesUpdateEvent with the fresh
 * sections so the drawer and the cart page re-render. Scoped to the cart; no libraries.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const SOURCE = 'hl-cart-addons';
let running = false;

/** @param {any} item */
const isAddon = (item) => item?.product_type === 'Add-on';

/**
 * @param {any[]} items
 * @returns {Record<string, number>} line key to the quantity it should have
 */
export function planAddonFixes(items) {
  /** @type {Record<string, number>} */
  const updates = {};
  if (!Array.isArray(items)) return updates;
  const byKey = new Map(items.map((i) => [i.key, i]));
  const byGroup = new Map();
  for (const i of items) {
    const g = i.properties?._hl_group;
    if (g && !isAddon(i)) byGroup.set(g, i);
  }
  for (const i of items) {
    if (!isAddon(i)) continue;
    const parentKey = i.parent_relationship?.parent_key;
    const parent = (parentKey && byKey.get(parentKey)) || byGroup.get(i.properties?._hl_group);
    const target = parent && !isAddon(parent) ? parent.quantity : 0;
    if (i.quantity !== target) updates[i.key] = target;
  }
  return updates;
}

function sectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
    .filter(Boolean);
}

/** @param {any[]} [items] */
async function fix(items) {
  if (running) return;
  running = true;
  try {
    if (!items) items = (await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json()).items;
    const updates = planAddonFixes(items || []);
    const keys = Object.keys(updates);
    if (keys.length === 0) return;

    const deferred = CartLinesUpdateEvent.createPromise();
    document.body.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: keys.map((id) => ({ id, quantity: updates[id] })),
        promise: deferred.promise,
      })
    );

    const base = window.Theme?.routes?.cart_update_url || '/cart/update';
    const res = await fetch(`${base}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: sectionIds().join(','), sections_url: window.location.pathname }),
    });
    const cart = await res.json();
    if (!res.ok || cart.status) {
      deferred.reject(new Error(cart.description || cart.message || 'Add-on sync failed'));
      return;
    }
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, sections: cart.sections, itemCount: cart.item_count, source: SOURCE, didError: false },
    });
  } catch (error) {
    console.warn('[hl-cart-addons]', error);
  } finally {
    running = false;
  }
}

/** @param {any} event */
function onCartUpdate(event) {
  event.promise
    ?.then((/** @type {any} */ result) => {
      const detail = result?.detail;
      if (!detail || detail.source === SOURCE || detail.didError) return;
      fix(Array.isArray(detail.items) ? detail.items : undefined);
    })
    .catch(() => {});
}

if (!window.__hlCartAddons) {
  window.__hlCartAddons = true;
  document.addEventListener(StandardEvents.cartLinesUpdate, onCartUpdate);
  const run = () => {
    if (document.querySelector('[data-hl-cart-fix]')) fix();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true });
  else run();
}
